// src/components/common/SentDocumentCopies.js — docpdf-8a4c1e73 (2026-10-28)
// "Emailed copies": the PDFs Spatchy kept of a credit note's emails, or of an estimate's emails sent with
// the PDF attached — exactly what the customer received. Office Staff and up only: firestore.rules let
// only them read documentSends, so for anyone else the list read is refused and nothing shows.
// View → getSentDocumentPdf returns the saved PDF's bytes; it is saved in this browser. There is no link.
import React, { useEffect, useRef, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FileText, Loader2 } from 'lucide-react';
import { db, functions } from '../../firebase/config';
import { formatDate, toJsDate } from '../../utils/formatters';
import { downloadPdfBytes } from '../../utils/pdfDownload';
import { sentCopyRows } from '../../utils/sentDocumentCopies';

const SentDocumentCopies = ({ orgId, kind, docId }) => {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const busyRef = useRef(false);

    useEffect(() => {
        let alive = true;
        if (!orgId || !docId) { setRows([]); return undefined; }
        getDocs(query(collection(db, `organizations/${orgId}/documentSends`), where('docId', '==', docId), where('kind', '==', kind)))
            .then((snap) => { if (alive) setRows(sentCopyRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })))); })
            .catch(() => { if (alive) setRows([]); }); // not office (rules refuse) or offline: show nothing
        return () => { alive = false; };
    }, [orgId, kind, docId]);

    if (!rows.length) return null;

    const view = async (sendId) => {
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(sendId); setError('');
        try {
            const res = await httpsCallable(functions, 'getSentDocumentPdf')({ sendId });
            downloadPdfBytes(res.data?.pdfBase64, res.data?.fileName);
        } catch (e) {
            setError(e?.message || 'The saved copy could not be opened. Please try again.');
        } finally {
            busyRef.current = false;
            setBusy('');
        }
    };

    return (
        <div className="mt-1 flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-600">Emailed copies</span>
            {rows.map((r) => (
                <span key={r.id} className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                    <span>{`Emailed ${r.sentAt ? formatDate(toJsDate(r.sentAt)) : ''} to ${r.to || 'the customer'}${r.ccCount ? ` (+${r.ccCount} CC)` : ''}`}</span>
                    <button onClick={() => view(r.id)} disabled={!!busy} aria-label={`View the PDF emailed ${r.sentAt ? formatDate(toJsDate(r.sentAt)) : ''}`.trim()}
                        className="inline-flex items-center gap-1 font-medium text-[#5F4EC9] hover:underline disabled:opacity-50">
                        {busy === r.id ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} View
                    </button>
                </span>
            ))}
            {error && <span role="status" className="text-xs text-red-600">{error}</span>}
        </div>
    );
};

export default SentDocumentCopies;
